/**
 * Data-source seam for the Document Studio tab.
 *
 * Reads and writes real Firestore documents by their full path. The editor works
 * on the raw stored document (no id injection, no Timestamp coercion) so what you
 * see is what is stored, and Save overwrites the document with exactly the JSON
 * shown. Access restrictions — which paths a user may open or edit — are layered
 * on later; today any valid document path is editable.
 */

import { getFirestoreDB } from "@risalabs_frontend_org/auth-wrapper";
import {
  collection,
  doc,
  documentId,
  endAt,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  setDoc,
  startAt,
} from "firebase/firestore";
import type { JsonObject, StudioDocument } from "../types";

/** High code point that bounds a document-id prefix range query (U+F8FF). */
const PREFIX_HIGH = "\uf8ff";
const SUGGESTION_LIMIT = 100;

/** Trim and strip leading/trailing slashes from a user-entered path. */
export const normalizePath = (path: string): string =>
  path.trim().replace(/^\/+|\/+$/g, "");

/** Split a normalized path into its non-empty segments. */
const segmentsOf = (path: string): string[] =>
  normalizePath(path).split("/").filter(Boolean);

/**
 * A Firestore document lives at an even, non-zero number of path segments
 * (collection/doc, collection/doc/sub/doc, ...). An odd count is a collection.
 */
export const isDocumentPath = (path: string): boolean => {
  const segments = segmentsOf(path);
  return segments.length > 0 && segments.length % 2 === 0;
};

/** A Firestore collection lives at an odd, non-zero number of path segments. */
export const isCollectionPath = (path: string): boolean => {
  const segments = segmentsOf(path);
  return segments.length > 0 && segments.length % 2 === 1;
};

/** Last path segment, used as the document's display label. */
export const labelFromPath = (path: string): string => {
  const segments = segmentsOf(path);
  return segments[segments.length - 1] ?? normalizePath(path);
};

/** Load a single document by its full path, or null if it does not exist. */
export const loadDocument = async (
  path: string,
): Promise<StudioDocument | null> => {
  const normalized = normalizePath(path);
  const snapshot = await getDoc(doc(getFirestoreDB(), normalized));
  if (!snapshot.exists()) {
    return null;
  }
  return {
    id: snapshot.id,
    label: labelFromPath(normalized),
    path: normalized,
    data: (snapshot.data() ?? {}) as JsonObject,
  };
};

/** Overwrite the document at the given path with the provided data. */
export const saveDocument = async (
  path: string,
  data: JsonObject,
): Promise<void> => {
  await setDoc(doc(getFirestoreDB(), normalizePath(path)), data);
};

/**
 * Validate a requested new-document location and resolve its full path.
 *
 * A new document lives at `<collection>/<id>` where the collection is any odd
 * segment path (an existing collection, or a brand-new one/sub-collection that
 * Firestore materialises on first write) and the id is a single segment.
 *
 * @input collectionPath: collection to create the document under (odd segments)
 * @input docId: the new document id (one segment, no slashes)
 * @return the normalized path and id, or a user-facing error message
 */
export const resolveNewDocumentPath = (
  collectionPath: string,
  docId: string,
): { path: string; id: string } | { error: string } => {
  const collectionSegment = normalizePath(collectionPath);
  const id = docId.trim();
  if (!isCollectionPath(collectionSegment)) {
    return {
      error:
        "Enter a collection path with an odd number of segments, e.g. configurations.",
    };
  }
  if (id === "" || id.includes("/")) {
    return { error: "Enter a document id without slashes." };
  }
  return { path: `${collectionSegment}/${id}`, id };
};

/**
 * Autocomplete: document ids in a collection whose id starts with `prefix`.
 *
 * Uses a bounded range query on the document id (the __name__ index, always
 * present — no composite index needed), so it stays cheap even on huge
 * collections. An empty prefix returns the first ids in id order.
 *
 * @input collectionPath: full path to a collection (odd segment count)
 * @input prefix: the partial id the user has typed
 * @input max: maximum ids to return
 * @return list of matching document ids (empty on any query error)
 */
export const suggestDocumentIds = async (
  collectionPath: string,
  prefix: string,
  max: number = SUGGESTION_LIMIT,
): Promise<string[]> => {
  const normalized = normalizePath(collectionPath);
  if (!isCollectionPath(normalized)) {
    return [];
  }
  try {
    const target = collection(getFirestoreDB(), normalized);
    const constraints = prefix
      ? [
          orderBy(documentId()),
          startAt(prefix),
          endAt(prefix + PREFIX_HIGH),
          limit(max),
        ]
      : [orderBy(documentId()), limit(max)];
    const snapshot = await getDocs(query(target, ...constraints));
    return snapshot.docs.map((entry) => entry.id);
  } catch {
    return [];
  }
};
